import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { aggridHeaderClass, pdaCellStyle } from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { usePDA1122_R01Content } from "./PDA1122_R01Content";
import { PDA1122_R01InputItem } from "./PDA1122_R01Dto";
import usePDA1122_R01Store from "./usePDA1122_R01Store";

type Row = PDA1122_R01InputItem & { inserted?: boolean };

const PDA1122_R01DetailTable = () => {
  const { selectedOrder, inputList, savedInputList } = usePDA1122_R01Store();
  const { handleRemoveInput } = usePDA1122_R01Content();
  const gridRef = useRef<AgGridReact<Row>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [, setHoveredRowIndex] = useState<number | null>(null);
  const [, setColumnState] = useState<ColumnState[] | null>(null);
  const paddedGridData = useMemo(
    () => [
      ...inputList.map((item) => ({ ...item, saved: false, inserted: true })),
      ...savedInputList.map((item) => ({ ...item, saved: true, inserted: false })),
    ],
    [inputList, savedInputList]
  );

  const columnDefs: ColDef<Row>[] = useMemo(
    () => [
      {
        headerName: "품명",
        field: "itemNm",
        valueGetter: (params) => params.data?.itemNm ?? "-",
        cellStyle: { textAlign: "left", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 1.5,
        minWidth: 40,
      },
      {
        headerName: "LOT No",
        field: "lotNo",
        valueGetter: (params) => params.data?.lotNo ?? "-",
        cellStyle: { textAlign: "left", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 120,
      },
      {
        headerName: "LOT수량",
        field: "lotQty",
        valueGetter: (params) =>
          params.data?.lotQty != null ? params.data.lotQty.toLocaleString() : "-",
        cellStyle: { textAlign: "right", justifyContent: "flex-end", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 0.9,
        minWidth: 60,
      },
      {
        headerName: "최초 입고일",
        field: "lotCreateDt",
        valueGetter: (params) => params.data?.lotCreateDt ?? "-",
        cellStyle: { textAlign: "center", ...pdaCellStyle },
        headerClass: aggridHeaderClass,
        flex: 1,
        minWidth: 100,
      },
    ],
    []
  );

  const handleRowClick = (row?: Row) => {
    if (!row?.lotNo || row.saved) return;
    if (window.confirm(`LOT ${row.lotNo} 을(를) 투입 목록에서 제거할까요?`)) {
      handleRemoveInput(row);
    }
  };

  const hasInputRows = paddedGridData.length > 0;
  return (
    <div className="flex-1 p-3 pt-0 flex w-full h-full flex-col overflow-hidden">
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={() => {}}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1122_R01_InputTable_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0">
          <Grid_Select<Row>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            onRowClicked={(event) => handleRowClick(event.data)}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1122_R01_InputTable_ColumnState"
            setColumnState={setColumnState}
          />
        </div>
      </GridContextWrapper>
      {selectedOrder && hasInputRows && (
        <div className="px-2 py-2 border-t border-gray-200 bg-white">
          <p className="text-sm text-gray-500 text-center">
            신규 행은 최상단에 표시됩니다 · 신규 행을 누르면 목록에서 제거됩니다
          </p>
        </div>
      )}
    </div>
  );
};

export default PDA1122_R01DetailTable;
